<template>
	<div ref="quickNav" class="space-y-6 hdn-editor-quicknav">
		<AppLink
			:to="{ name: 'LoanApplicationDetailsPage' }"
			icon="arrow_back"
			class="mt-2"
		>
			Terug naar overzicht
		</AppLink>

		<section class="space-y-2">
			<h4>Informatie</h4>
			<LoanApplicationEditorInfoPills
				:version-number
				:is-revision
				:has-unsaved-changes
			/>
		</section>

		<section class="space-y-2">
			<div class="flex items-center gap-1">
				<h4>Filters</h4>
			</div>
			<OptionInput v-model="selectedFilter" :options="filterOptions" />
		</section>

		<section class="space-y-2">
			<h4>Acties</h4>
			<div v-if="canDebug" class="flex flex-col items-start gap-1">
				<input type="file" accept="application/xml" @change="setDocToUpload" />
				<AppButton v-if="docToUpload" variant="secondary" @click="uploadDoc">
					Uploaden
				</AppButton>
			</div>
			<AppButtonGroup>
				<AppButton
					variant="secondary"
					:disabled="isValidating"
					@click="onVerifyClick"
				>
					Verifiëren
				</AppButton>
				<AppButton variant="primary" @click.prevent="emit('submit')">
					Versturen
				</AppButton>
				<AppButton v-if="canDebug" variant="secondary" @click="onDownloadClick">
					Downloaden
				</AppButton>
			</AppButtonGroup>
			<p
				v-if="showValidationSuccess"
				class="text-sm text-[var(--color-success)]"
			>
				Aanvraag compleet
			</p>
		</section>

		<details ref="navigation-section-ref" open class="-ml-3 cursor-pointer">
			<summary>
				<h4 class="inline">Navigatie</h4>
			</summary>
			<div class="pt-2 ml-3 space-y-2">
				<LoanApplicationFormNavigationLinks v-if="formKitNode" :links />
			</div>
		</details>

		<details ref="error-section-ref" class="-ml-3 cursor-pointer">
			<summary>
				<h4 v-if="hasValidated" class="inline">
					<span class="text-green-600">{{ validFieldCount }}</span
					>/<span>{{ totalFieldCount }}</span> correct<span v-if="errorCount"
						>,
						<span :class="colorClasses">{{ errorCount }} opmerkingen</span>
					</span>
				</h4>
				<h4 v-else class="inline">Opmerkingen</h4>
			</summary>
			<div class="pt-2 ml-3 space-y-2">
				<OurFormKitSummary
					v-if="formKitNode"
					:node="formKitNode"
					@summaries-updated="onSummariesUpdated"
				/>

				<div v-if="providerErrors.length" class="pt-2 border-t border-gray-100">
					<h5 class="text-xs font-medium text-gray-500 mb-1">
						Provider-specifieke opmerkingen
					</h5>
					<ul class="space-y-1">
						<li
							v-for="err in providerErrors"
							:key="err.code + err.message"
							class="text-sm text-warn"
						>
							⚠ {{ err.message }}
						</li>
					</ul>
				</div>

				<div v-if="generalErrors.length" class="pt-2 border-t border-gray-100">
					<h5 class="text-xs font-medium text-gray-500 mb-1">
						Algemene HDN-validatie
					</h5>
					<ul class="space-y-1">
						<li
							v-for="err in generalErrors"
							:key="err.code + err.message"
							class="text-sm text-warn"
						>
							⚠ {{ err.message }}
						</li>
					</ul>
				</div>
			</div>
		</details>
	</div>
</template>

<script lang="ts" setup>
import type { FormKitSummaryMessage } from "@/components/input-formkit/OurFormKitSummary";
import { OurFormKitSummary } from "@/components/input-formkit/OurFormKitSummary";
import type { ValidationError } from "@/generatedApi";
import {
	getVisibleErrors,
	groupFormLevelErrors,
	splitFormLevelErrors,
} from "@/helpers/loanApplicationValidationErrors";
import { computed, ref, useTemplateRef, watch } from "vue";
import { useActiveAnchor } from "./loan-application-form/outline";
import type { LinkType } from "@/components/loan-application/loan-application-form/LinkType";
import type { FormKitNode } from "@formkit/core";
import { showFormField } from "@/helpers/application-form/navigation";
import { afterValidation } from "@/helpers/application-form/formkitUtils";
import type { AvailableOptionTypes } from "@/composables/useOptionsToArray";
import { useFeatureFlag } from "@/composables/useFeatureFlag";

interface Props {
	formKitNode: FormKitNode;
	versionNumber: number;
	isRevision: boolean;
	hasUnsavedChanges: boolean;
	links: LinkType[];
}
const props = defineProps<Props>();

interface Emits {
	submit: [payload: void];
	download: [payload: void];
	upload: [payload: File];
}
const emit = defineEmits<Emits>();

const canDebug = useFeatureFlag("hdn-force-send-enabled");

// activate active anchor on quicknav
const quickNav = ref<HTMLElement | null>(null);
useActiveAnchor(quickNav);

const docToUpload = ref<File | null>(null);
function setDocToUpload(event: Event) {
	const target = event.target as HTMLInputElement;
	docToUpload.value = target.files?.[0] ?? null;
}

function uploadDoc() {
	if (!docToUpload.value) return;
	emit("upload", docToUpload.value);
}

// create options for filter ui
const selectedFilter = defineModel<undefined | "relevant">(undefined);
const filterOptions: AvailableOptionTypes = [
	{ label: "Alle velden", value: undefined },
	{ label: "Ingevuld met opmerkingen", value: "relevant" },
];

const isValidating = ref(false);
const showValidationSuccess = ref(false);

watch(
	() => props.formKitNode,
	(node, _, onCleanup) => {
		const receipt = node.on("commit", () => {
			showValidationSuccess.value = false;
		});

		onCleanup(() => {
			node.off(receipt);
		});
	},
	{ immediate: true },
);

async function onVerifyClick() {
	showValidationSuccess.value = false;
	formLevelErrors.value = [];

	afterValidation(props.formKitNode, () => {
		if (firstErrorSummary.value) {
			showFormField(firstErrorSummary.value.id, true);
		}
	});

	isValidating.value = true;
	try {
		const validationErrors: ValidationError[] =
			(await props.formKitNode.context?.fns.manualValidate?.()) ?? [];

		formLevelErrors.value = splitFormLevelErrors(
			validationErrors,
			(path) => !!props.formKitNode.at(path),
		);

		showValidationSuccess.value =
			getVisibleErrors(validationErrors).length === 0;

		updateErrorState();
	} finally {
		isValidating.value = false;
	}
}

function onDownloadClick() {
	emit("download");
}

// for error section of sidebar
const errorSection = useTemplateRef("error-section-ref");
const navigationSection = useTemplateRef("navigation-section-ref");
const errorCount = ref(0);
const totalFieldCount = ref(0);
const invalidFieldCount = ref(0);
const hasValidated = ref(false);
const validFieldCount = computed(() =>
	Math.max(0, totalFieldCount.value - invalidFieldCount.value),
);
const firstErrorSummary = ref<FormKitSummaryMessage | undefined>(undefined);

const fieldSummaries = ref<FormKitSummaryMessage[]>([]);
const formLevelErrors = ref<ValidationError[]>([]);
const grouped = computed(() => groupFormLevelErrors(formLevelErrors.value));
const providerErrors = computed(() => grouped.value.providerErrors);
const generalErrors = computed(() => grouped.value.generalErrors);

function countInputFields(node: FormKitNode): number {
	let count = 0;
	if (node.type === "input" && node.props.type !== "hidden") {
		count = 1;
	}
	for (const child of node.children as FormKitNode[]) {
		if (typeof child === "object" && child.children) {
			count += countInputFields(child);
		}
	}
	return count;
}

function updateErrorState() {
	errorCount.value = fieldSummaries.value.length + formLevelErrors.value.length;
	invalidFieldCount.value = new Set(fieldSummaries.value.map((s) => s.id)).size;
	totalFieldCount.value = countInputFields(props.formKitNode);
	hasValidated.value = true;

	if (errorSection.value && errorCount.value > 0) {
		errorSection.value.open = true;
	}
	if (navigationSection.value && errorCount.value > 0) {
		navigationSection.value.open = false;
	}

	firstErrorSummary.value =
		fieldSummaries.value.length > 0 ? fieldSummaries.value[0] : undefined;
}

function onSummariesUpdated(summaries: FormKitSummaryMessage[]) {
	fieldSummaries.value = summaries;
	updateErrorState();
}
const colorClasses = computed(() => ({ "text-warn": errorCount.value }));
</script>
